/**
 * `/settings` — the signed-in person's own preferences.
 *
 * Open to every signed-in person: it changes only how things look and read for
 * them. Saving re-reads the session, and the workspace remounts when the region
 * changes, so the confirmation is a toast rather than text on this page.
 *
 * @module router/settings/SettingsRoute
 */

import React, { useState } from "react";

import { UserSettings } from "@/components/settings/UserSettings";
import { ErrorAlert, useToast } from "@/components/ui";
import { begin } from "@/utils/begin";
import { resolveRegion } from "@/utils/format-instant";

import { useWorkspaceOutletContext } from "../workspace-context";

export function SettingsRoute(): React.ReactElement {
  const { client, session, tenantRegion, refreshSession } = useWorkspaceOutletContext();
  const toast = useToast();
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = (next: Parameters<React.ComponentProps<typeof UserSettings>["onSave"]>[0]): void => {
    setPending(true);
    setError(null);
    begin(() => client.updateMyPreferences(next))
      .then(() => refreshSession())
      .then(() => {
        toast({ message: "Settings saved", tone: "success" });
      })
      .catch((cause: unknown) => {
        setError(cause instanceof Error ? cause.message : "Your settings could not be saved.");
      })
      .finally(() => {
        setPending(false);
      });
  };

  return (
    <>
      {error ? (
        <ErrorAlert data-testid="settings-error" className="mb-4">
          {error}
        </ErrorAlert>
      ) : null}
      <UserSettings
        preferences={session.user}
        tenantDefault={resolveRegion(tenantRegion, undefined)}
        pending={pending}
        onSave={save}
      />
    </>
  );
}
